import React, { useState, useEffect } from 'react';
import { CloseIcon } from 'components/common/icons';
import { useAttachmentDeleteMutation } from 'api/global';
import { useAttachmentFileDeleteMutation } from 'api/files';
import { DeleteIcon } from 'components/common/icons/Delete';
import { convertEnumsToReadableStrings } from 'utils/helpers';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { Button, List, Row, Col, Typography, FMGModal, Image, Popconfirm } from 'components';
import styles from './ItemAttachmentsModal.module.scss';

const { Text } = Typography;

interface Attachment {
  id: string;
  fileName: string;
  extension: string;
  size: number;
  attachmentType: string;
  files: Record<string, string>;
}

interface ItemAttachmentsModalProps {
  visible: boolean;
  attachments: Attachment[];
  onClose: () => void;
  onAttachmentDeleted?: () => void;
}

export const ItemAttachmentsModal: React.FC<ItemAttachmentsModalProps> = ({
  visible,
  attachments,
  onClose,
  onAttachmentDeleted,
}) => {
  const { handleSuccess, handleError } = useNotificationMessages();
  const [deleteAttachment] = useAttachmentDeleteMutation();
  const [deleteAttachmentFile] = useAttachmentFileDeleteMutation();
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>(attachments);

  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  const getFileIcon = (attachment: Attachment) => {
    const types = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    const ext = attachment.extension.toLowerCase();

    if (ext === 'pdf' || attachment.fileName.endsWith('.pdf')) {
      return (
        <div className={styles.fileIcon} style={{ backgroundColor: '#e74c3c', color: 'white' }}>
          PDF
        </div>
      );
    } else if (types.some((type) => type.includes(ext))) {
      return (
        <div className={styles.filePreview}>
          <Image src={attachment.files?.ATTACHMENTS} width={48} height={48} style={{ objectFit: 'cover' }} />
        </div>
      );
    } else if (
      attachment.fileName.endsWith('.csv') ||
      attachment.fileName.endsWith('.xlsx') ||
      attachment.fileName.endsWith('.xls')
    ) {
      return (
        <div className={styles.fileIcon} style={{ backgroundColor: '#27ae60', color: 'white' }}>
          {attachment.fileName.split('.').pop()?.toUpperCase()}
        </div>
      );
    } else {
      return (
        <div className={styles.fileIcon} style={{ color: '#3498db', backgroundColor: '#e6f7ff' }}>
          FILE
        </div>
      );
    }
  };

  const handleDelete = async (attachmentId: string, fileName: string) => {
    try {
      // Step 1: Delete attachment record
      const response: any = await deleteAttachment({
        entityName: 'PURCHASE_ORDER_ITEM',
        id: attachmentId,
        service: 'wrd',
      });

      // Check for error response from RTK Query
      if (response.error) {
        handleError(response.error);
        return;
      }

      if (!response?.data?.success) {
        handleError(response);
        return;
      }

      // Step 2: If not inherited, delete the file from FSS
      if (!response.data.data?.inherited) {
        const fileResponse: any = await deleteAttachmentFile({
          id: attachmentId,
          name: fileName,
        });

        if (fileResponse.error) {
          handleError(fileResponse.error);
          return;
        }

        if (!fileResponse?.data?.success) {
          handleError(fileResponse);
          return;
        }
      }

      handleSuccess('Attachment deleted successfully');

      // Remove attachment from local state
      setLocalAttachments((prevAttachments) => prevAttachments.filter((att) => att.id !== attachmentId));

      // Trigger callback to refresh parent data
      if (onAttachmentDeleted) {
        onAttachmentDeleted();
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  return (
    <>
      <FMGModal
        title="Attachments"
        open={visible}
        onCancel={onClose}
        width={500}
        footer={<></>}
        className={styles.attachmentsModal}
        closeIcon={<CloseIcon />}
        centered
      >
        {localAttachments.length === 0 ? (
          <div className={styles.emptyState}>
            <Text type="secondary">No attachments available</Text>
          </div>
        ) : (
          <List
            dataSource={localAttachments}
            className={styles.attachmentsList}
            renderItem={(item: Attachment) => {
              const canDelete = item.attachmentType !== 'WAREHOUSE_IDENTIFIER';

              return (
                <List.Item
                  className={styles.listItem}
                  actions={
                    canDelete
                      ? [
                          <Popconfirm
                            key="delete"
                            title="Delete attachment"
                            description="Are you sure you want to delete this attachment?"
                            onConfirm={() => handleDelete(item.id, item.fileName)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                          >
                            <Button className={styles.deleteBtn} size="small" type="link" icon={<DeleteIcon />} />
                          </Popconfirm>,
                        ]
                      : []
                  }
                >
                  <Row wrap={false} align="middle" style={{ width: '100%', paddingLeft: 8 }}>
                    <Col flex="48px">{getFileIcon(item)}</Col>
                    <Col flex="320px">
                      <Text ellipsis={{ tooltip: true }} style={{ height: 32, marginLeft: 12 }}>
                        {item.attachmentType && (
                          <span className={styles.optionTag}>{convertEnumsToReadableStrings(item.attachmentType)}</span>
                        )}{' '}
                        {item.fileName}
                      </Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: 12 }}>
                        {item.size ? Math.ceil(item.size / 1000) + ' KB' : 'Size N/A'}
                      </div>
                    </Col>
                  </Row>
                </List.Item>
              );
            }}
          />
        )}
      </FMGModal>
    </>
  );
};
